import React, { forwardRef, useCallback } from 'react'
import Text from 'core/elements/Text'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import { useDropzone } from 'react-dropzone'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import withFormContext from 'core/components/validatedForm/withFormContext'

interface Props {
  onChange: (value: any) => void
  imageUpdater?: (value: any) => void
  value?: string
  imageData?: string
  id?: string
  validations?: any[]
  hasError?: boolean
  errorMessage?: string
  maxWidth?: number
  maxHeight?: number
}

const useStyles = makeStyles<Theme, any>((theme: Theme) => ({
  dropzone: {
    padding: 16,
    background: theme.components.card.background,
    border: `1px dashed ${theme.components.card.passiveText}`,
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    position: 'relative',
  },
  uploadIcon: {
    color: theme.components.card.passiveText,
  },
  dropzoneText: {
    marginTop: 8,
  },
  imagePreview: {
    maxHeight: ({ maxHeight }) => (maxHeight ? maxHeight : 44),
    maxWidth: ({ maxWidth }) => (maxWidth ? maxWidth : 48),
    objectFit: 'contain',
  },
  trash: {
    position: 'absolute',
    top: 12,
    right: 12,
    color: theme.components.card.passiveText,
    cursor: 'pointer',
  },
  errorText: {
    color: theme.components.graph.error,
  },
}))

const arrayBufferToBase64 = (buffer) => {
  let binary = ''
  const bytes = new Uint8Array(buffer)
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return window.btoa(binary)
}

const Warning = ({ children }) => {
  const classes = useStyles({})
  return (
    <Text variant="body1" className={classes.errorText}>
      {children}
    </Text>
  )
}

const ImageDrop: React.ComponentType<Props> = forwardRef<HTMLElement, Props>(
  (props, ref: React.Ref<HTMLDivElement>) => {
    const maxWidth = props.maxWidth || 48
    const maxHeight = props.maxHeight || 44

    const { onChange, imageUpdater, value, imageData, hasError, errorMessage } = props

    const classes = useStyles({ maxWidth, maxHeight })
    const clearFile = (e) => {
      e.stopPropagation()
      imageUpdater('')
      onChange({})
    }

    const onDrop = useCallback((acceptedFiles) => {
      const file = acceptedFiles[0]

      const reader = new FileReader()

      reader.onabort = () => console.log('file reading was aborted')
      reader.onerror = () => console.log('file reading has failed')
      reader.onload = () => {
        const buffer = reader.result
        const base64 = arrayBufferToBase64(buffer)
        imageUpdater(base64)
        onChange(file)
      }
      reader.readAsArrayBuffer(file)
    }, [])
    const { getRootProps, getInputProps } = useDropzone({
      onDrop,
      accept: 'image/jpeg, image/png',
      maxFiles: 1,
    })

    return (
      <div>
        <div className={classes.dropzone} {...getRootProps()}>
          <input {...getInputProps()} data-testid="image-drop--input-0" />
          <div>
            {imageData && (
              <>
                <FontAwesomeIcon className={classes.trash} onClick={clearFile} solid>
                  trash-alt
                </FontAwesomeIcon>
                <img
                  className={classes.imagePreview}
                  src={`data:image/png;base64,${imageData}`}
                  data-testid="image-drop--img-0"
                />
              </>
            )}
            {!imageData && (
              <>
                <FontAwesomeIcon className={classes.uploadIcon} size="2x" solid>
                  file-upload
                </FontAwesomeIcon>
                <div className={classes.dropzoneText}>
                  <Text variant="body2">
                    <b>Drop</b> file here to upload
                  </Text>
                </div>
              </>
            )}
          </div>
        </div>
        {hasError && <Warning>{errorMessage}</Warning>}
      </div>
    )
  },
)

export default withFormContext(ImageDrop) as React.FC<Props>
